import { FaTimes, FaCheck } from 'react-icons/fa';
import { CgTrash } from 'react-icons/cg';
import { Flex, Text, Button, Box } from 'rebass';
import { UserAgent } from '@quentin-sommer/react-useragent';
import AvatarEditor from 'react-avatar-editor';
import DropZone from 'react-dropzone';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import ScrollLock from 'react-scrolllock';
import * as loadImage from 'blueimp-load-image';

import { acceptedFor } from '../../../../story-media/helper';
import AvatarPreview from './image';
import ZoomSlider from 'app/form/formik/cover/ZoomSlider';
import { PillButton } from 'app/buttons/styled';
import useEntity from 'entities/hooks/use-entity';
import { destroy } from 'utils/api';
import useEntityActionCreators from 'entities/hooks/use-entity-action-creators';
import { renderPositionForVGBImage } from 'collections/watch-me-grow/components/ImageEditor/helper';
import { getBestImageVersion } from 'utils/image-versions';

const CircleButton = ({ children, size, ...props }) => (
  <Button
    type="button"
    borderRadius="50%"
    style={{ height: size, width: size, position: 'relative' }}
    px={0}
    py={0}
    {...props}
  >
    <Box
      css="position: absolute; top: 50%; left: 50%; transform: translateY(-50%) translateX(-50%); line-height: 1; display: inline-flex;"
      as="span"
    >
      {children}
    </Box>
  </Button>
);
CircleButton.propTypes = { children: PropTypes.node.isRequired, size: PropTypes.number };
CircleButton.defaultProps = { size: 24 };

const initialState = {
  zoom: 1,
  image: null,
  staged: false,
  preview: null,
  initialFile: null,
  positions: null,
};

const CoverField = ({
  width,
  currentCover,
  form,
  circular,
  hasConfirm,
  field,
  hasEditButton,
  destroyCover,
  showCroppingParams,
  value,
}) => {
  const [state, setState] = useState(initialState);
  const editorRef = useRef(null);
  const ref = useRef(null);

  const { entity } = useEntity({ type: 'images', id: currentCover });

  const { entity: canvasImage } = useEntity({ type: 'images', id: entity?.['canvas-image']?.id });
  const { receiveDeletion } = useEntityActionCreators();
  const { zoom, image, staged, preview, initialFile, positions } = state;
  const imageDetails = Object.keys(canvasImage).length > 0 ? entity : null;

  useEffect(() => {
    if ((value || imageDetails) && Object.keys((value || imageDetails)).length > 0) {
      setState((prev) => ({
        ...prev,
        positions: ((value || imageDetails)?.['left'] || (value || imageDetails)?.['top']) ? {
          x: (value || imageDetails)?.['left'] || 0,
          y: (value || imageDetails)?.['top'] || 0,
        } : undefined,
        zoom: (value || imageDetails)?.zoom || 1
      }))
    }

  }, [value, imageDetails])

  // Cropping-params mode: keep the crop/zoom synced to the form while the editor is
  // open, so the surrounding "Save" captures it without a separate "Confirm Crop".
  useEffect(() => {
    if (!showCroppingParams || !staged) return;
    form.setFieldValue(field.name, {
      file: state.initialFile || null,
      zoom: +zoom,
      'crop-left': positions?.x ?? 0,
      'crop-top': positions?.y ?? 0,
    });
  }, [showCroppingParams, staged, zoom, positions, state.initialFile]);

  useEffect(() => {
    getInitialPreview();
    return () => {
      if (image) URL.revokeObjectURL(image);
    };
  }, []);

  const getInitialPreview = () => {
    const { value } = field;
    if (value) {
      setState((prev) => ({ ...prev, preview: value }));
    }
  };

  const handleChange = async () => {
    const imageCrops = editorRef.current?.state?.image;
    const updatedPositions = editorRef.current.getCroppingRect();

    const cropMeta = {
      zoom: +zoom,
      'crop-left': positions?.x || updatedPositions?.x || 0,
      'crop-top': positions?.y || updatedPositions?.y || 0,
      left: positions?.x || imageCrops?.x || 0,
      top: positions?.y || imageCrops?.y || 0,
      'crop-left': `${updatedPositions?.x}`,
      'crop-top': `${updatedPositions?.y}`,
      'crop-width': `${updatedPositions.width}%`,
      'crop-height': `${updatedPositions.height}%`,
    };

    // Cropping-params mode (e.g. family tree): keep the ORIGINAL file + crop params
    // only. We deliberately skip exporting the canvas so we never read a cross-origin
    // <img> (which would taint the canvas / require CORS) — the original image simply
    // needs to render in the editor.
    if (showCroppingParams) {
      form.setFieldValue('avatar', { file: state.initialFile, ...cropMeta });
      setState((prev) => ({ ...prev, preview: prev.image, staged: false, isEditing: false }));
      return;
    }

    const canvas = editorRef.current.getImage();
    const file = await new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          const newImage = blob;
          newImage.name = (initialFile || field.value).name || canvasImage?.filename || 'cover.jpg';
          newImage.lastModifiedAt = new Date();
          resolve(newImage);
        },
        (initialFile || field.value).type,
        100
      );
    });

    form.setFieldValue('avatar-canvas-image', { file, ...cropMeta });
    setState((prev) => ({
      ...prev,
      preview: file,
      staged: false,
      isEditing: false,
    }));
  };

  const handleConfirm = () => {
    const { value } = field;
    setState((prev) => ({
      ...prev,
      preview: staged ? value : null,
      ...(!staged && initialState),
    }));
  };

  const handleCancel = () => {

    if (entity?.id) {
      destroy(`/v2/media/${entity?.id}`)
      receiveDeletion({ entityType: 'images', id: entity.id })
    }
    if (canvasImage?.id) {
      destroy(`/v2/media/${canvasImage?.id}`)
      receiveDeletion({ entityType: 'images', id: canvasImage.id })
    }

    form.setFieldValue(field.name, null);
    setState(initialState);
  };

  const handleMount = async (file) => {
    setState((prev) => ({ ...prev, initialFile: file, mounting: true }));
    const processedFile = await new Promise((resolve) => {
      loadImage(
        file,
        (canvas) => {
          canvas.toBlob(
            (blob) => {
              const nextFile = blob;
              nextFile.lastModifiedAt = new Date();
              nextFile.name = file.name;
              resolve(blob);
            },
            file.type,
            100
          );
        },
        {
          orientation: true,
          canvas: true,
        }
      );
    });

    form.setFieldValue(field.name, processedFile);
    setState({
      ...initialState,
      initialFile: file,
      image: URL.createObjectURL(processedFile),
      mounting: false,
      preview: null,
      staged: true,
    });
  };


  // ── Mobile only: pinch-to-zoom (drag-to-pan is already handled natively by
  // ReactAvatarEditor's onPositionChange). Desktop keeps the slider + wheel zoom.
  const isTouchDevice =
    typeof window !== 'undefined' &&
    ('ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0);
  const useTouchGestures = width < 500 && isTouchDevice;
  const zoomMin = 1;
  const zoomMax = 5;

  // Keep the latest zoom/position readable inside the (once-attached) native listeners.
  const zoomRef = useRef(zoom);
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  const positionsRef = useRef(positions);
  useEffect(() => { positionsRef.current = positions; }, [positions]);
  const pinchRef = useRef(null);
  const panRef = useRef(null);
  const gestureActiveRef = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || !useTouchGestures) return undefined;

    const applyZoom = (raw) => {
      const clamped = Math.min(zoomMax, Math.max(zoomMin, raw));
      // setZoom(posterAttributes ? Math.round(clamped) : Number(clamped.toFixed(2)));
      setState((prev) => ({ ...prev, zoom: Number(clamped.toFixed(2)) }));

    };
    const distance = (touches) =>
      Math.hypot(
        touches[0].clientX - touches[1].clientX,
        touches[0].clientY - touches[1].clientY,
      );

    // iOS Safari AND iOS Chrome (both WebKit) implement pinch via non-standard
    // GestureEvents and usually withhold the 2-finger touchmove sequence, so the
    // touch-distance path below never fires there — these handle those browsers.
    const onGestureStart = (e) => {
      e.preventDefault();
      gestureActiveRef.current = true;
      pinchRef.current = { startZoom: Number(zoomRef.current) || zoomMin };
    };
    const onGestureChange = (e) => {
      e.preventDefault();
      if (pinchRef.current) applyZoom(pinchRef.current.startZoom * e.scale);
    };
    const onGestureEnd = (e) => {
      e.preventDefault();
      gestureActiveRef.current = false;
      pinchRef.current = null;
    };

    // Touch-distance pinch for non-WebKit touch browsers (e.g. Android Chrome).
    const onTouchStart = (e) => {
      if (e.touches.length === 2 && !gestureActiveRef.current) {
        panRef.current = null;
        pinchRef.current = {
          startDist: distance(e.touches) || 1,
          startZoom: Number(zoomRef.current) || zoomMin,
        };
      } else if (e.touches.length === 1) {
        panRef.current = { lastX: e.touches[0].clientX, lastY: e.touches[0].clientY };
      }
    };
    const onTouchMove = (e) => {
      if (gestureActiveRef.current) return; // WebKit gesture handlers own zoom
      if (e.touches.length === 2 && pinchRef.current && pinchRef.current.startDist) {
        // We drive zoom from the pinch; stop the browser's native page zoom.
        // (React's onTouchMove is passive, so this must be a native listener.)
        e.preventDefault();
        const ratio = distance(e.touches) / pinchRef.current.startDist;
        applyZoom(pinchRef.current.startZoom * ratio);
        return;
      }
      if (e.touches.length === 1) {
        // Single-finger drag-to-pan. We implement it ourselves (mirroring
        // react-avatar-editor's position math) because ScrollLock's TouchScrollable can
        // stopPropagation the touchmove before it reaches the editor's own document
        // listener, so its built-in pan never fires. stopPropagation here keeps the two
        // from fighting (its handler would otherwise double-apply if it did receive it).
        const t = e.touches[0];
        if (!panRef.current) {
          panRef.current = { lastX: t.clientX, lastY: t.clientY };
          return;
        }
        const img = editorRef.current && editorRef.current.state && editorRef.current.state.image;
        if (!img || !img.width || !img.height) return;
        e.preventDefault();
        e.stopPropagation();
        const scale = Number(zoomRef.current) || 1;
        const base = positionsRef.current || { x: img.x, y: img.y };
        const next = {
          x: base.x - (t.clientX - panRef.current.lastX) / (img.width * scale),
          y: base.y - (t.clientY - panRef.current.lastY) / (img.height * scale),
        };
        setState((prev) => ({ ...prev, positions: next }));
        panRef.current = { lastX: t.clientX, lastY: t.clientY };
      }
    };
    const endPinch = (e) => {
      if (e.touches.length < 2) pinchRef.current = null;
      if (e.touches.length === 0) panRef.current = null;
    };

    el.addEventListener('gesturestart', onGestureStart, { passive: false });
    el.addEventListener('gesturechange', onGestureChange, { passive: false });
    el.addEventListener('gestureend', onGestureEnd, { passive: false });
    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', endPinch, { passive: false });
    el.addEventListener('touchcancel', endPinch, { passive: false });
    return () => {
      el.removeEventListener('gesturestart', onGestureStart);
      el.removeEventListener('gesturechange', onGestureChange);
      el.removeEventListener('gestureend', onGestureEnd);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', endPinch);
      el.removeEventListener('touchcancel', endPinch);
    };
    // `staged` is required: the ref'd div only mounts once the editor is staged, so the
    // effect must re-run then to attach the listeners (and detach when it unmounts).
  }, [useTouchGestures, zoomMin, zoomMax, staged]);

  const height = circular ? width : width / 2;

  return (
    <>
      <Flex flexDirection="column">
        {!staged && destroyCover && (
          <CircleButton
            ml={width - 12}
            mb={-12}
            bg="red"
            type="button"
            onClick={destroyCover}
            css="display: block; z-index: 1;"
          >
            <FaTimes color="white" />
          </CircleButton>
        )}
        <UserAgent android>
          {(isAndroid) => (
            <DropZone
              onDrop={(dropped) => handleMount(dropped[0])}
              disableClick={staged}
              accept={isAndroid ? 'image/*' : acceptedFor.images}
              style={{ margin: '0 auto', cursor: 'pointer' }}
              multiple={false}
            >
              {({ getRootProps, getInputProps }) => (
                <ScrollLock isActive={staged}>
                  <div style={staged ? { margin: 'auto' } : {}} {...getRootProps()}>
                    {!staged && <input {...getInputProps()} />}
                    {staged && (
                      // Pinch/gesture listeners attach to THIS inner div. It must not be
                      // the ScrollLock child — react-scrolllock wraps that child in
                      // TouchScrollable and clones it with its own ref, which would
                      // clobber `ref` and leave ref.current null (so pinch never worked).
                      <div ref={ref} style={useTouchGestures ? { touchAction: 'none' } : undefined}>
                        <AvatarEditor
                          ref={editorRef}
                          image={image}
                          width={circular ? width : width - 40}
                          height={circular ? width : height - 20}
                          border={20}
                          borderRadius={circular ? width / 2 : 0}
                          scale={zoom}
                          // Only request CORS when we actually export the canvas (normal
                          // mode). In showCroppingParams mode we never read the canvas, so
                          // crossOrigin just forces a CORS fetch the CDN doesn't satisfy —
                          // throwing on the remote URL that image.js renders fine without it.
                          crossOrigin={showCroppingParams ? undefined : 'anonymous'}
                          position={positions}
                          onPositionChange={(pos) =>
                            setState((prev) => ({ ...prev, positions: pos }))
                          }
                          onWheel={(e) => {
                            e.preventDefault();
                            const scaleDelta = e.deltaY > 0 ? -0.01 : 0.01;
                            const newScale = zoom + scaleDelta;
                            if (newScale <= 5 && newScale >= 1) {
                              setState((prev) => ({ ...prev, zoom: newScale }));
                            }
                          }}
                        />
                      </div>
                    )}
                    {!staged && !preview && currentCover && (
                      <div css="position: relative">
                        <AvatarPreview
                          height={height}
                          width={width}
                          canvasId={entity?.['canvas-image']?.id}
                          src={entity.state === 'stalled_upload' ? entity?.['unprocessed-url'] : entity.tmpUrl || getBestImageVersion(entity).url}
                          alt="Preview"
                          borderRadius={circular ? '50%' : '0px'}
                          circular={circular}
                          pose={state.mounting ? 'loading' : null}
                          hasEditButton={hasEditButton}
                          onEditPress={(imgSrc, imageBlob) => {
                            // form.setFieldValue(field.name, imageBlob);
                            setState((prev) => ({
                              ...prev,
                              image: imgSrc,
                              isEditing: true,
                              mounting: false,
                              preview: null,
                              staged: true,
                            }));
                          }}
                          showCroppingParams={showCroppingParams ? {
                            width: circular ? width : width - 40,
                            height: circular ? width : height - 20,
                            borderRadius: circular ? width / 2 : 0,
                            zoom,
                            position: renderPositionForVGBImage(positions),
                          } : undefined}
                        />
                      </div>
                    )}
                    {(preview || (!staged && !currentCover)) && (
                      <AvatarPreview
                        height={height}
                        width={width}
                        src={preview}
                        alt="Preview"
                        borderRadius={circular ? '50%' : '0px'}
                        circular={circular}
                        pose={state.mounting ? 'loading' : null}
                        hasEditButton={hasEditButton}
                        onEditPress={() => {
                          setState((prev) => ({
                            ...prev,
                            staged: true,
                            preview: null,
                            isEditing: true,
                          }));
                        }}
                        showCroppingParams={showCroppingParams ? {
                          width: circular ? width : width - 40,
                          height: circular ? width : height - 20,
                          borderRadius: circular ? width / 2 : 0,
                          zoom,
                          position: renderPositionForVGBImage(positions),
                        } : undefined}
                      />
                    )}
                  </div>
                </ScrollLock>
              )}
            </DropZone>
          )}
        </UserAgent>
        {staged ? (
          <Flex m="auto" flexDirection="column">
            {!useTouchGestures && (
              <ZoomSlider
                id="zoom_control"
                zoom={zoom}
                onChange={(e) => setState((prev) => ({ ...prev, zoom: e.target.value }))}
                min="1"
                max="5"
                step="0.01"
              />
            )}
            <Flex mt={3} justifyContent={'space-between'}>
              <Flex mr={[2, 0]} onClick={handleCancel} justifyContent="center" alignItems="center" sx={{ cursor: 'pointer' }}>
                <CgTrash size={20} color="#64748B" />
                <Text
                  textAlign="center"
                  ml="4px"
                  color="#64748B"
                  fontFamily="Poppins"
                  fontSize="16px"
                  letterSpacing="0.36px"
                  lineHeight="150%"
                  alignItems="center"
                  fontWeight="500"
                >
                  Remove Photo
                </Text>
              </Flex>
              {hasConfirm && (
                <PillButton
                  onClick={() => {
                    handleChange();
                    // handleConfirm();
                  }}
                  type="button"
                  primary
                >
                  <FaCheck style={{ marginRight: '4px' }} />
                  Confirm Crop
                </PillButton>
              )}
            </Flex>
          </Flex>
        ) : null}
      </Flex>
    </>
  );
};

CoverField.propTypes = {
  width: PropTypes.number,
  field: PropTypes.shape({
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(File),
      PropTypes.instanceOf(Blob),
    ]),
  }).isRequired,
  form: PropTypes.shape({}).isRequired,
  currentCover: PropTypes.string,
  circular: PropTypes.bool,
  hasConfirm: PropTypes.bool,
  hasEditButton: PropTypes.bool,
  destroyCover: PropTypes.func,
};

CoverField.defaultProps = {
  width: 150,
  currentCover: '',
  circular: false,
  hasConfirm: true,
  hasEditButton: true,
  destroyCover: null,
};

export default CoverField;